/**
 * Sites on our own platform in Saidiseire: the CRM's side of the contract, and
 * the rules the screens need (docs/saidiseire-plaan.md, item K).
 *
 * A HAND-WRITTEN MIRROR of `apps/api/src/modules/crm-monitoring/platform/types.ts`.
 * `monitoringPlatform.test.ts` reads that file and compares the field names of
 * every interface, the same arrangement as `monitoring.ts`.
 *
 * React-free: which sites the desk still offers, what a finding is called and
 * which tone it wears, and how a share of good visits is worded are decided
 * here and tested without mounting anything.
 */
import { api } from "./api";
import { MONITORING_PATH, MONITOR_SITES_PATH } from "./monitoring";
import type { PolicyLine } from "./monitoringPlans";

// ── Contract (mirrors crm-monitoring/platform/types.ts) ─────────────────────────

/** Whose site it is: the platform's own, one of our own account's, or a customer's. */
export type PlatformOwner = "host" | "internal" | "customer";

export interface PlatformSiteOption {
  siteId: string;
  name: string;
  /** The site's short name in the admin. */
  slug: string;
  owner: PlatformOwner;
  /** The address to watch: the primary verified host, else the first verified one. */
  host: string;
  /** Every verified host of the site, the primary first. */
  hosts: string[];
  /** The monitor that already watches one of those hosts, or null. */
  monitorId: string | null;
  /** An active CRM client whose website is on one of those hosts, or null. */
  client: { id: string; name: string } | null;
}

export interface PlatformSitesResponse {
  /** Active sites with at least one verified host; not watched ones first, then by name. */
  sites: PlatformSiteOption[];
}

export interface PlatformDomainView {
  host: string;
  primary: boolean;
  verified: boolean;
}

export type PlatformFindingLevel = "critical" | "warning";

export interface PlatformFinding {
  code: "site_suspended" | "site_closed" | "no_homepage" | "host_not_on_site" | "domain_unverified" | "domain_grace";
  level: PlatformFindingLevel;
  /** Hosts, a date or a reason, depending on the code; null when the code says it all. */
  detail: string | null;
}

export interface PlatformVitalView {
  metric: "LCP" | "INP" | "CLS";
  /** Visits rated good. */
  good: number;
  /** Visits measured. */
  total: number;
}

export interface PlatformSiteView {
  siteId: string;
  name: string;
  slug: string;
  owner: PlatformOwner;
  /** active | suspended | closed */
  status: string;
  suspendedReason: string | null;
  createdAt: string;
  /** The account's platform plan code, or null (the platform's own site has none). */
  planCode: string | null;
  planName: Record<string, string>;
  subscriptionStatus: string | null;
  domains: PlatformDomainView[];
  customDomainGraceUntil: string | null;
  pages: {
    published: number;
    drafts: number;
    homepage: boolean;
    lastChangeAt: string | null;
  };
  /** Published blog posts. */
  posts: number;
  forms: {
    answers30d: number;
    unread: number;
    held30d: number;
  };
  restorePoints: {
    total: number;
    lastAt: string | null;
    /** Hours between automatic restore points; 0 = off. */
    everyHours: number;
  };
  media: { files: number; bytes: number };
  members: number;
  traffic: { visitors: number; pageviews: number };
  notFound7d: number;
  vitals: PlatformVitalView[];
}

export interface PlatformDetailResponse {
  /** Null when the site no longer exists on the platform. */
  site: PlatformSiteView | null;
  findings: PlatformFinding[];
  platform: { version: string; startedAt: string };
}

// ── Routes ───────────────────────────────────────────────────────────────────

export const PLATFORM_SITES_PATH = `${MONITORING_PATH}/platform-sites`;
export const platformPath = (monitorId: string): string => `${MONITOR_SITES_PATH}/${encodeURIComponent(monitorId)}/platform`;

export const listPlatformSites = (signal?: AbortSignal): Promise<PlatformSitesResponse> =>
  api.get<PlatformSitesResponse>(PLATFORM_SITES_PATH, signal);

export const getPlatformSite = (monitorId: string, signal?: AbortSignal): Promise<PlatformDetailResponse> =>
  api.get<PlatformDetailResponse>(platformPath(monitorId), signal);

// ── Rules ────────────────────────────────────────────────────────────────────

/** The sites nobody watches yet: what the desk's card and the add dialog offer. */
export function unwatched(sites: readonly PlatformSiteOption[]): PlatformSiteOption[] {
  return sites.filter((s) => s.monitorId === null);
}

/** Every finding code the API can send (`PlatformFinding.code`), each with a sentence. */
export const PLATFORM_FINDING_CODES = [
  "site_suspended",
  "site_closed",
  "no_homepage",
  "host_not_on_site",
  "domain_unverified",
  "domain_grace",
] as const;

/** A finding's sentence key; a code this browser does not know gets the generic one. */
export function platformFindingKey(code: string): string {
  return (PLATFORM_FINDING_CODES as readonly string[]).includes(code)
    ? `monitoring.platform.finding.${code}`
    : "monitoring.platform.finding.other";
}

export function platformFindingTone(level: PlatformFindingLevel): "lt" | "wt" {
  return level === "critical" ? "lt" : "wt";
}

/** The site's own state as a pill: closed and suspended are alarms, anything else we do not know is muted. */
export function platformStatusTone(status: string): "ok" | "lt" | "mut" {
  if (status === "active") return "ok";
  return status === "suspended" || status === "closed" ? "lt" : "mut";
}

/** The key of the site's state; an unknown one reads as itself. */
export function platformStatusKey(status: string): string | null {
  return status === "active" || status === "suspended" || status === "closed" ? `monitoring.platform.status.${status}` : null;
}

/** The share of good visits, 0..100 in whole percent; null when nothing was measured. */
export function goodShare(v: Pick<PlatformVitalView, "good" | "total">): number | null {
  if (v.total <= 0) return null;
  return Math.round((Math.min(v.good, v.total) / v.total) * 100);
}

/**
 * Google's own bar for a Core Web Vital: at least 75 % of visits good. Under
 * 50 % reads as an alarm; in between as a warning.
 */
export function vitalTone(v: Pick<PlatformVitalView, "good" | "total">): "ok" | "wt" | "lt" | "mut" {
  const share = goodShare(v);
  if (share === null) return "mut";
  if (share >= 75) return "ok";
  return share >= 50 ? "wt" : "lt";
}

/** The site's platform plan in the language asked, else Estonian, else any, else its code; null without a plan. */
export function platformPlanName(site: Pick<PlatformSiteView, "planCode" | "planName">, locale: string): string | null {
  if (!site.planCode) return null;
  return site.planName[locale] ?? site.planName.et ?? Object.values(site.planName)[0] ?? site.planCode;
}

/** The package lines that are about a WordPress site only: the plugin, and what the plugin reads. */
const WORDPRESS_ONLY: readonly PolicyLine["key"][] = ["reporter", "backupBefore", "backupDaily", "dbCleanup"];

/**
 * A package's lines for a site on our own platform. The WordPress-only ones are
 * left out: no plugin runs there, and listing "WordPress report" under such a
 * site reads as a promise nobody can keep.
 */
export function platformPolicyLines(lines: readonly PolicyLine[]): PolicyLine[] {
  return lines.filter((line) => !WORDPRESS_ONLY.includes(line.key));
}
